import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { fetchUserAppAPI } from "../utils/api";

export default function AddAddressModal({ show, onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [plotNo, setPlotNo] = useState("");
  const [locality, setLocality] = useState("");
  const [area, setArea] = useState("");
  const [pincode, setPincode] = useState("");

  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");
  const [selectedLocalityId, setSelectedLocalityId] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");

  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [localitiesList, setLocalitiesList] = useState([]);
  const [areasList, setAreasList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      fetchStates();
      // Pre-fill user phone/name from localStorage if available
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const u = JSON.parse(storedUser);
          const fullName = ((u.user_first_name || "") + " " + (u.user_last_name || "")).trim();
          if (fullName) setName(fullName);
          if (u.user_mobile) setPhone(u.user_mobile);
        } catch (e) {}
      }
    }
  }, [show]);

  const fetchStates = async () => {
    try {
      const res = await fetchUserAppAPI("get-state", { method: "GET" });
      if (res && res.status === 200 && Array.isArray(res.result)) {
        setStatesList(res.result);
      }
    } catch (err) {
      console.error("Error fetching states:", err);
    }
  };

  const fetchCities = async (stateId) => {
    setCitiesList([]);
    setLocalitiesList([]);
    setAreasList([]);
    setSelectedCityId("");
    setSelectedLocalityId("");
    setSelectedAreaId("");
    setLocality("");
    setArea("");
    setPincode("");

    if (!stateId) return;

    try {
      const res = await fetchUserAppAPI("get-city-by-state", {
        method: "POST",
        body: JSON.stringify({ state_id: stateId }),
      });
      if (res && res.status === 200 && Array.isArray(res.result)) {
        setCitiesList(res.result);
      }
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const fetchLocalities = async (cityId) => {
    setLocalitiesList([]);
    setAreasList([]);
    setSelectedLocalityId("");
    setSelectedAreaId("");
    setLocality("");
    setArea("");
    setPincode("");

    if (!cityId) return;

    try {
      const res = await fetchUserAppAPI("get-locality-by-city", {
        method: "POST",
        body: JSON.stringify({ city_id: cityId }),
      });
      if (res && res.status === 200 && Array.isArray(res.result)) {
        setLocalitiesList(res.result);
      }
    } catch (err) {
      console.error("Error fetching localities:", err);
    }
  };

  const fetchAreas = async (localityId) => {
    setAreasList([]);
    setSelectedAreaId("");
    setArea("");
    setPincode("");

    if (!localityId) return;

    try {
      const res = await fetchUserAppAPI("get-area-by-locality", {
        method: "POST",
        body: JSON.stringify({ locality_id: localityId }),
      });
      if (res && res.status === 200 && Array.isArray(res.result)) {
        setAreasList(res.result);
      }
    } catch (err) {
      console.error("Error fetching areas:", err);
    }
  };

  const handleStateChange = (e) => {
    const sId = e.target.value;
    setSelectedStateId(sId);
    fetchCities(sId);
  };

  const handleCityChange = (e) => {
    const cId = e.target.value;
    setSelectedCityId(cId);
    fetchLocalities(cId);
  };

  const handleLocalityChange = (e) => {
    const locVal = e.target.value;
    if (!locVal) {
      setSelectedLocalityId("");
      setLocality("");
      fetchAreas("");
      return;
    }
    const foundLoc = localitiesList.find(
      (l) => String(l.locality_id) === String(locVal) || l.locality_name === locVal
    );
    if (foundLoc) {
      setSelectedLocalityId(foundLoc.locality_id);
      setLocality(foundLoc.locality_name);
      fetchAreas(foundLoc.locality_id);
    } else {
      setSelectedLocalityId("");
      setLocality(locVal);
      fetchAreas("");
    }
  };

  const handleAreaChange = (e) => {
    const areaVal = e.target.value;
    if (!areaVal) {
      setSelectedAreaId("");
      setArea("");
      setPincode("");
      return;
    }
    const foundArea = areasList.find(
      (a) => String(a.area_id) === String(areaVal) || a.area_name === areaVal
    );
    if (foundArea) {
      setSelectedAreaId(foundArea.area_id);
      setArea(foundArea.area_name);
      if (foundArea.area_pincode) setPincode(foundArea.area_pincode);
    } else {
      setSelectedAreaId("");
      setArea(areaVal);
    }
  };

  const resetForm = () => {
    setTitle("");
    setName("");
    setPhone("");
    setPlotNo("");
    setLocality("");
    setArea("");
    setPincode("");
    setSelectedStateId("");
    setSelectedCityId("");
    setSelectedLocalityId("");
    setSelectedAreaId("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      setError("Please login first.");
      return;
    }

    const user = JSON.parse(storedUser);
    setLoading(true);
    setError("");

    try {
      const payload = {
        user_id: user.user_id,
        address_default: 1,
        address_title: title || "Home",
        address_name: name,
        address_phone: phone,
        address_plot_no: plotNo,
        address_locility: locality,
        address_area: area,
        address_city: selectedCityId,
        address_state: selectedStateId,
        address_pincode: pincode,
        address_latitude: "",
        address_longitude: "",
      };

      const response = await fetchUserAppAPI("add-address", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response && response.status === 200) {
        const newAddress =
          response.result && response.result[0]
            ? response.result[0]
            : response.result;

        // Auto select and notify
        if (newAddress) {
          localStorage.setItem("selectedAddress", JSON.stringify(newAddress));
          window.dispatchEvent(new Event("addressChanged"));
        }

        resetForm();
        if (onSuccess) onSuccess(newAddress);
        if (onClose) onClose();
      } else {
        setError(response?.msg || "Failed to save address.");
      }
    } catch (err) {
      console.error("Error adding address:", err);
      setError("An error occurred while saving address.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.55)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        className="card border-0 shadow-lg"
        style={{
          maxWidth: "730px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: "16px",
          backgroundColor: "#ffffff",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
          style={{ backgroundColor: "#13505b", color: "#ffffff", borderRadius: "16px 16px 0 0" }}
        >
          <h5 className="mb-0   d-flex align-items-center gap-2">
            <AddOutlinedIcon /> Add New Address
          </h5>
          <button
            type="button"
            className="btn btn-sm btn-link text-white text-decoration-none p-0"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="card-body p-4 popup_address">
          {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="add_form_inputs">
                    <label className="form-label small   text-secondary mb-1"> Address Title * </label>
                    <input type="text" className="form-control" placeholder=" " value={title} onChange={(e) => setTitle(e.target.value)}  required/>
                </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                  <label className="form-label small   text-secondary mb-1">  Contact Name * </label>
                  <input type="text" className="form-control"  placeholder=" " value={name} onChange={(e) => setName(e.target.value)} required />
                </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                  <label className="form-label small   text-secondary mb-1"> Contact Phone * </label>
                  <input type="tel"  className="form-control" placeholder=" "  value={phone}  onChange={(e) => setPhone(e.target.value)} required />
                </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                   <label className="form-label small   text-secondary mb-1">  Plot / Flat / House No. * </label>
                  <input type="text" className="form-control" placeholder=" " value={plotNo} onChange={(e) => setPlotNo(e.target.value)} required />
                </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                    <label className="form-label small   text-secondary mb-1"> State *  </label>
                    <select className="form-select" value={selectedStateId} onChange={handleStateChange}  required >
                      <option value="">Select State</option>
                      {statesList.map((st) => (
                        <option key={st.state_id} value={st.state_id}>
                          {st.state_name}
                        </option>
                      ))}
                    </select>
                </div> 
              </div>

              <div className="col-md-6">
                 <div className="add_form_inputs">
                   <label className="form-label small   text-secondary mb-1"> City * </label>
                  <select className="form-select" value={selectedCityId} onChange={handleCityChange} required  disabled={!selectedStateId} >
                    <option value="">Select City</option>
                    {citiesList.map((ct) => (
                      <option key={ct.city_id} value={ct.city_id}>
                        {ct.city_name}
                      </option>
                    ))}
                  </select>
                 </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                   <label className="form-label small   text-secondary mb-1">
                  Locality / Sector *
                </label>
                {localitiesList.length > 0 ? (
                  <select
                    className="form-select"
                    value={selectedLocalityId || locality}
                    onChange={handleLocalityChange}
                    required
                  >
                    <option value="">Select Locality</option>
                    {localitiesList.map((loc) => (
                      <option key={loc.locality_id} value={loc.locality_id}>
                        {loc.locality_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    placeholder=" "
                    value={locality}
                    onChange={(e) => setLocality(e.target.value)}
                    required
                  />
                )}                  
                  </div> 
              </div>

              <div className="col-md-6">
                <div className="add_form_inputs">
                     <label className="form-label small   text-secondary mb-1">
                  Area / Landmark *
                </label>
                {areasList.length > 0 ? (
                  <select
                    className="form-select"
                    value={selectedAreaId || area}
                    onChange={handleAreaChange}
                    required
                  >
                    <option value="">Select Area</option>
                    {areasList.map((ar) => (
                      <option key={ar.area_id} value={ar.area_id}>
                        {ar.area_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    placeholder=" "
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    required
                  />
                )}                  
                </div> 
              </div>

              <div className="col-md-12">
                <div className="add_form_inputs">
                  <label className="form-label small   text-secondary mb-1">
                  Pincode *
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder=" "
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  required
                />  
                </div> 
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4 pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn px-4 text-white"
                style={{ backgroundColor: "#13505b", fontWeight: "600" }}
                disabled={loading}
              >
                {loading ? "Saving Address..." : "Save Address"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
